import { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, collectionGroup, getDocs, query, where, onSnapshot, QuerySnapshot, DocumentData } from 'firebase/firestore';

interface QueueCounts {
  approvals: number;
  workbench: number;
  review: number;
  qa: number;
  schemaNeeded: number;
  datasetRequestsCount: number;
  supportRequestsCount: number;
  trimmingCount: number;
  videoWorkbenchCount: number;
  androidTestersCount: number;
  payoutRequestsCount: number;
}

export function useQueueCounts() {
  const [counts, setCounts] = useState<QueueCounts>({
    approvals: 0,
    workbench: 0,
    review: 0,
    qa: 0,
    schemaNeeded: 0,
    datasetRequestsCount: 0,
    supportRequestsCount: 0,
    trimmingCount: 0,
    videoWorkbenchCount: 0,
    androidTestersCount: 0,
    payoutRequestsCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Record<string, any>>({});

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        console.log('🔍 [useQueueCounts] Fetching queue counts...');
        
        // Get Task Approvals count (pending and flagged submissions)
        const submissionsSnap = await getDocs(collection(db, "submissions"));
        console.log('📊 [useQueueCounts] Total submissions:', submissionsSnap.docs.length);
        
        const approvalsCount = submissionsSnap.docs.filter(d => {
          const status = d.data().status;
          return (status === "pending" || status === "flagged") && d.data().videoStage !== 'trimming';
        }).length;

        // Get Workbench count (submissions that need labeling with detection schemas)
        // First get all approved/needs_relabeling submissions
        const workbenchSubmissions = submissionsSnap.docs.filter(d => {
          const status = d.data().status;
          return status === "approved" || status === "needs_relabeling";
        });

        // Then get all tasks to check for detection schemas
        const tasksSnap = await getDocs(collection(db, "tasks"));
        const tasksData = tasksSnap.docs.reduce((acc, doc) => {
          acc[doc.id] = doc.data();
          return acc;
        }, {} as Record<string, any>);

        // Store tasks in state for real-time updates
        setTasks(tasksData);

        // Filter workbench submissions to only include those with detection schemas and not in QA Review
        const workbenchCount = workbenchSubmissions.filter(d => {
          const submission = d.data();
          // Exclude submissions that are in QA Review
          if (submission.qaReviewStatus === 'pending' || submission.qaReviewStatus === 'saved_for_later') {
            return false;
          }
          const task = tasksData[submission.taskId];
          return task?.detection?.enabled && task?.detection?.schemaRef?.id;
        }).length;

        // Get Review count (submissions awaiting review)
        const reviewCount = submissionsSnap.docs.filter(d => {
          const status = d.data().status;
          return status === "labeled";
        }).length;

        // Get QA counts (only Active items, not Save for Later)
        // QA Workbench - only 'pending', not 'saved_for_later'
        const qaReviewCount = submissionsSnap.docs.filter(d => {
          const data = d.data();
          return data.qaReviewStatus === 'pending';
        }).length;

        // QA Approvals - only 'pending', not 'saved_for_later'
        const qaApprovalCount = submissionsSnap.docs.filter(d => {
          const data = d.data();
          return data.qaApprovalStatus === 'pending';
        }).length;

        // QA Auto Validation
        const qaAutoValidationCount = submissionsSnap.docs.filter(d => {
          const data = d.data();
          return data.status === "approved" && 
                 data.labelingDetails?.labelingFlagged === true;
        }).length;

        // QA Final Review - only 'pending', not 'saved_for_later'
        const qaFinalReviewCount = submissionsSnap.docs.filter(d => {
          const data = d.data();
          return data.qaFinalReviewStatus === 'pending';
        }).length;

        const qaTotal = qaReviewCount + qaApprovalCount + qaAutoValidationCount + qaFinalReviewCount;

        // Get Schema Needed count (approved submissions without schemas)
        const schemaNeededCount = submissionsSnap.docs.filter(d => {
          const submission = d.data();
          if (submission.status !== 'approved') return false;
          const task = tasksData[submission.taskId];
          if (!task) return true; // If task not found, assume needs schema
          // Check for detection schema
          if (task.detection?.enabled && task.detection?.schemaRef?.id) return false;
          // Check for legacy schema
          if (task.labelSchemaRef?.id) return false;
          return true; // No schema found
        }).length;

        const videoWorkbenchCount = submissionsSnap.docs.filter(d => {
          const sub = d.data();
          if (sub.taskType !== 'Video') return false;
          if (sub.status !== 'approved' && sub.status !== 'needs_relabeling') return false;
          if (!sub.trimmedVideoUrl) return false;
          const task = tasksData[sub.taskId];
          if (!task) return false;
          return task.detection?.enabled && task.detection?.schemaRef?.id;
        }).length;

        const datasetRequestsSnap = await getDocs(
          query(collection(db, "datasetRequests"), where("status", "==", "new"))
        );
        const datasetRequestsCount = datasetRequestsSnap.docs.length;

        const supportRequestsSnap = await getDocs(
          query(collection(db, 'supportRequests'), where('status', '==', 'open'))
        );
        const supportRequestsCount = supportRequestsSnap.docs.length;

        console.log('📊 [useQueueCounts] Counts:', {
          approvals: approvalsCount,
          workbench: workbenchCount,
          review: reviewCount,
          qa: qaTotal,
          schemaNeeded: schemaNeededCount,
          datasetRequestsCount,
          supportRequestsCount,
          qaBreakdown: {
            qaApprovals: qaApprovalCount,
            qaAutoValidation: qaAutoValidationCount,
            qaWorkbench: qaReviewCount,
            qaFinalReview: qaFinalReviewCount,
            total: qaTotal
          }
        });

        setCounts({
          approvals: approvalsCount,
          workbench: workbenchCount,
          review: reviewCount,
          qa: qaTotal,
          schemaNeeded: schemaNeededCount,
          datasetRequestsCount,
          supportRequestsCount,
          trimmingCount: submissionsSnap.docs.filter(doc => {
            const d = doc.data();
            return d.taskType === 'Video' && d.videoStage === 'trimming';
          }).length,
          videoWorkbenchCount,
          androidTestersCount: 0,
          payoutRequestsCount: 0
        });
      } catch (error) {
        console.error('❌ [useQueueCounts] Error fetching queue counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();

    // Set up real-time listeners for updates
    const unsubscribeApprovals = onSnapshot(
      query(
        collection(db, "submissions"),
        where("status", "in", ["pending", "flagged"])
      ),
      (snapshot: QuerySnapshot<DocumentData>) => {
        const count = snapshot.docs.filter(
          (doc) => doc.data().videoStage !== 'trimming'
        ).length;
        setCounts(prev => ({ ...prev, approvals: count }));
      }
    );

    // Set up tasks listener
    const unsubscribeTasks = onSnapshot(
      collection(db, "tasks"),
      (snapshot: QuerySnapshot<DocumentData>) => {
        const tasksData = snapshot.docs.reduce((acc, doc) => {
          acc[doc.id] = doc.data();
          return acc;
        }, {} as Record<string, any>);
        setTasks(tasksData);
      }
    );

    const unsubscribeWorkbench = onSnapshot(
      query(collection(db, "submissions"), where("status", "in", ["approved", "needs_relabeling"])),
      (snapshot: QuerySnapshot<DocumentData>) => {
        // For now, just use the raw count until tasks are loaded
        // The proper filtering will be applied in the useEffect when tasks change
        setCounts(prev => ({ ...prev, workbench: snapshot.docs.length }));
      }
    );

    const unsubscribeReview = onSnapshot(
      query(collection(db, "submissions"), where("status", "==", "labeled")),
      (snapshot: QuerySnapshot<DocumentData>) => {
        setCounts(prev => ({ ...prev, review: snapshot.docs.length }));
      }
    );

    // Set up QA count listener (only Active items, not Save for Later)
    const unsubscribeQA = onSnapshot(
      collection(db, "submissions"),
      (snapshot: QuerySnapshot<DocumentData>) => {
        // QA Workbench - only 'pending', not 'saved_for_later'
        const qaReviewCount = snapshot.docs.filter(d => {
          const data = d.data();
          return data.qaReviewStatus === 'pending';
        }).length;

        // QA Approvals - only 'pending', not 'saved_for_later'
        const qaApprovalCount = snapshot.docs.filter(d => {
          const data = d.data();
          return data.qaApprovalStatus === 'pending';
        }).length;

        // QA Auto Validation
        const qaAutoValidationCount = snapshot.docs.filter(d => {
          const data = d.data();
          return data.status === "approved" && 
                 data.labelingDetails?.labelingFlagged === true;
        }).length;

        const qaTotal = qaReviewCount + qaApprovalCount + qaAutoValidationCount;
        setCounts(prev => ({ ...prev, qa: qaTotal }));
      }
    );

    // Set up Schema Needed count listener
    const unsubscribeSchemaNeeded = onSnapshot(
      query(collection(db, "submissions"), where("status", "==", "approved")),
      (snapshot: QuerySnapshot<DocumentData>) => {
        // This will be recalculated when tasks change
        // For now, set a placeholder that will be updated in the tasks effect
      }
    );

    const unsubscribeDatasetRequests = onSnapshot(
      query(collection(db, "datasetRequests"), where("status", "==", "new")),
      (snapshot: QuerySnapshot<DocumentData>) => {
        setCounts(prev => ({ ...prev, datasetRequestsCount: snapshot.docs.length }));
      }
    );

    const unsubscribeSupportRequests = onSnapshot(
      query(collection(db, 'supportRequests'), where('status', '==', 'open')),
      (snapshot: QuerySnapshot<DocumentData>) => {
        setCounts(prev => ({ ...prev, supportRequestsCount: snapshot.docs.length }));
      }
    );

    const unsubscribePayouts = onSnapshot(
      query(
        collectionGroup(db, 'withdrawals'),
        where('type', '==', 'payout'),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        setCounts(prev => ({ ...prev, payoutRequestsCount: snapshot.docs.length }));
      },
      (err) => { console.error('payouts count listener error:', err); }
    );

    const unsubscribeVideoWorkbench = onSnapshot(
      query(
        collection(db, 'submissions'),
        where('status', 'in', ['approved', 'needs_relabeling']),
        where('taskType', '==', 'Video')
      ),
      (snap) => {
        const count = snap.docs.filter(d => {
          const data = d.data();
          return data.trimmedVideoUrl;
        }).length;
        setCounts(prev => ({ ...prev, videoWorkbenchCount: count }));
      }
    );

    const trimmingQuery = query(
      collection(db, 'submissions'),
      where('status', 'in', ['pending', 'flagged', 'approved']),
      where('taskType', '==', 'Video'),
      where('videoStage', '==', 'trimming')
    );
    const unsubscribeTrimming = onSnapshot(trimmingQuery, (snap) => {
      setCounts(prev => ({ ...prev, trimmingCount: snap.docs.length }));
    });

    const unsubscribeAndroidTesters = onSnapshot(
      collection(db, "androidTesters"),
      (snapshot: QuerySnapshot<DocumentData>) => {
        const count = snapshot.docs.filter(
          (doc) => doc.data().added !== true
        ).length;
        setCounts(prev => ({ ...prev, androidTestersCount: count }));
      }
    );

    return () => {
      unsubscribeApprovals();
      unsubscribeTasks();
      unsubscribeWorkbench();
      unsubscribeReview();
      unsubscribeQA();
      unsubscribeSchemaNeeded();
      unsubscribeDatasetRequests();
      unsubscribeSupportRequests();
      unsubscribePayouts();
      unsubscribeTrimming();
      unsubscribeVideoWorkbench();
      unsubscribeAndroidTesters();
    };
  }, []);

  // Recalculate workbench and schema needed counts when tasks change
  useEffect(() => {
    if (Object.keys(tasks).length === 0) return;

    const recalculateCounts = async () => {
      try {
        console.log('🔍 [useQueueCounts] Recalculating counts with tasks:', Object.keys(tasks).length);
        
        // Get approved/needs_relabeling submissions for workbench
        const workbenchSubmissionsSnap = await getDocs(
          query(collection(db, "submissions"), where("status", "in", ["approved", "needs_relabeling"]))
        );
        
        // Get approved submissions for schema needed
        const approvedSubmissionsSnap = await getDocs(
          query(collection(db, "submissions"), where("status", "==", "approved"))
        );
        
        const workbenchCount = workbenchSubmissionsSnap.docs.filter(d => {
          const submission = d.data();
          // Exclude submissions that are in QA Review
          if (submission.qaReviewStatus === 'pending' || submission.qaReviewStatus === 'saved_for_later') {
            return false;
          }
          const task = tasks[submission.taskId];
          const hasDetection = task?.detection?.enabled && task?.detection?.schemaRef?.id;
          return hasDetection;
        }).length;

        const schemaNeededCount = approvedSubmissionsSnap.docs.filter(d => {
          const submission = d.data();
          const task = tasks[submission.taskId];
          if (!task) return true; // If task not found, assume needs schema
          // Check for detection schema
          if (task.detection?.enabled && task.detection?.schemaRef?.id) return false;
          // Check for legacy schema
          if (task.labelSchemaRef?.id) return false;
          return true; // No schema found
        }).length;

        console.log('📊 [useQueueCounts] Recalculated counts:', { workbench: workbenchCount, schemaNeeded: schemaNeededCount });
        setCounts(prev => ({ ...prev, workbench: workbenchCount, schemaNeeded: schemaNeededCount }));
      } catch (error) {
        console.error('❌ [useQueueCounts] Error recalculating counts:', error);
      }
    };

    recalculateCounts();
  }, [tasks]);

  return { counts, loading };
}
